import {ColumnDef, createColumnHelper} from '@tanstack/react-table'
import {FC, useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {suppressScrollTopOnce} from '../../../../_metronic/helpers'
import {showSwal} from '../../../../ui-kit'
import {Card, Row, Col, Form, Button, Spinner, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {
  createAgreement,
  deleteAgreementContentById,
  exportAgreementDetail,
  getAgreementById,
  getOptionAgreements,
  updateAgreementById,
} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {AgreementDetail} from './core/_models'
import {
  buildAgreementFormEntries,
  getErrorMessage,
  parseSources,
  AgreementSource,
  deriveFieldsFromParties,
  WORLD_PARTY,
} from './core/_transforms'
import './TradeDataAgreementDetailPage.scss'
import Select, {MultiValue} from 'react-select'
import DataTable from '../../../../ui-kit/DataTable'
import {AgreementChapter} from '../trade-data-agreement-chapter/core/_models'
import {useAuth} from '../../../modules/auth'
import AgreementDetailView from './components/AgreementDetailView'
import ModalAddSources from './components/ModalAddSources'
import ModalArticleText from './components/ModalArticleText'
import ModalAuditLog from '../activity-logs/components/ModalAuditLog'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import NoPermissionCard from '../../../../_metronic/layout/components/permission/NoPermissionCard'
import {getISO2ByISO3} from '../../../libs/misc'
import {AGREEMENT_STATUS_OPTIONS, WTO_AGREEMENT_TYPE} from '../../../libs/constant'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

const ReactSwal = withReactContent(Swal)
const confirmDialog = (title: string) => {
  return ReactSwal.fire({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    customClass: {
      title: 'fw-bold',
      cancelButton: 'btn btn-danger btn-sm',
      confirmButton: 'btn btn-success btn-sm',
    },
    padding: '1rem',
  })
}

// Paired with the .resizable-multiselect wrapper class: container/control
// stretch to fill whatever height the user drags the wrapper to (instead of
// staying at their own natural height and leaving dead space below the
// chips), and valueContainer scrolls internally once its content is taller
// than that dragged height.
const multiSelectResizableStyles = {
  container: (base: any) => ({...base, flex: 1, minHeight: 0}),
  control: (base: any) => ({...base, height: '100%'}),
  valueContainer: (base: any) => ({...base, flexWrap: 'wrap', overflowY: 'auto'}),
}

const columnHelper = createColumnHelper<AgreementChapter>()
const sourceColumnHelper = createColumnHelper<AgreementSource>()

const TradeDataAgreementDetailPage: FC<{subject: string}> = ({subject}) => {
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {currentUser} = useAuth()

  // const countries = [{ value: 'EUN', label: 'European Union (EUN)' }, ...countryOptions]

  const {idAgreement} = useParams()
  const isDetailMode = Boolean(idAgreement)
  const isAddMode = !idAgreement

  const [isEditMode, setIsEditMode] = useState(false)
  const [isLoadingAgreement, setIsLoadingAgreement] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [agreementType, setAgreementType] = useState([])
  const [agreementNotification, setAgreementNotification] = useState([])
  const [agreementComposition, setAgreementComposition] = useState([])
  const [agreementDocumentType, setAgreementDocumentType] = useState([])
  const [countries, setCountries] = useState<any[]>([])

  const [importFile, setImportFile] = useState<File | null>(null)
  const [agreementDetail, setAgreementDetail] = useState<AgreementDetail>({
    acronym: '',
    agreement: '',
    type: [],
    wto_rta_id: '',
    status: '',
    notification: [],
    date_signed: '',
    date_into_force: '',
    date_notification: '',
    end_implementation: '',
    date_inactive: '',
    composition: '',
    subregion: '',
    parties_wto: '',
    crossregional: '',
    language: '',
    sources: '',
    date_xml_created: '',
    date_xml_edited: '',
    xml_file: '',
    xml_edited_comments: '',
    text_available: '',
    document_type: null,
    parties_escap: '',
    services_schedules: '',
    desta_id: '',
    iso: [], //refer to parties
  })
  // `region` is a purely client-computed display value derived from the
  // selected party countries — it is never stored/round-tripped by the
  // backend, so it lives in local state rather than on `agreementDetail`.
  const [region, setRegion] = useState('')
  const [pendingChapters, setPendingChapters] = useState([])

  const [showSourceModal, setShowSourceModal] = useState(false)
  const [showAuditLogModal, setShowAuditLogModal] = useState(false)
  const [sources, setSources] = useState<AgreementSource[]>([])
  const [sourceAlias, setSourceAlias] = useState('')
  const [sourceLink, setSourceLink] = useState('')

  const handleAddSource = () => {
    if (!sourceAlias.trim() || !sourceLink.trim()) return

    setSources((prev) => [
      ...prev,
      {
        id: `${Date.now()}_${Math.random()}`, // collision-resistant local id
        alias: sourceAlias.trim(),
        link: sourceLink.trim(),
      },
    ])

    // reset input
    setSourceAlias('')
    setSourceLink('')
  }

  const handleDeleteSource = (id: string) => {
    setSources((prev) => prev.filter((item) => item.id !== id))
  }

  const sourceColumns: ColumnDef<AgreementSource, any>[] = useMemo(
    () => [
      sourceColumnHelper.accessor('alias', {
        header: 'Alias',
        cell: (info) => info.getValue() || '-',
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left fw-semibold',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),

      sourceColumnHelper.accessor('link', {
        header: 'Link',
        cell: (info) => {
          const value = info.getValue()
          return value ? (
            <a href={value} target='_blank' rel='noreferrer' className='text-link'>
              {value}
            </a>
          ) : (
            '-'
          )
        },
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left text-break',
        },
        enableSorting: false,
        enableGlobalFilter: true,
      }),

      sourceColumnHelper.accessor('id', {
        header: '',
        cell: (info) => {
          const id = info.getValue()

          return isEditMode ? (
            <Button
              variant='link'
              className='text-danger p-0'
              onClick={() => handleDeleteSource(id)}
            >
              Delete
            </Button>
          ) : (
            <a href={sources.find((s) => s.id === id)?.link} target='_blank' rel='noreferrer'>
              Open
            </a>
          )
        },
        meta: {
          headerClass: 'text-end',
          cellClass: 'text-end',
        },
        enableSorting: false,
        enableGlobalFilter: false,
      }),
    ],
    [isEditMode, sources]
  )

  // table chapters
  const [dataTable, setDataTable] = useState<AgreementChapter[]>([])

  // Clicking a row opens its article text; the action column is excluded.
  // The rows are kept as the user sees them (sorted, filtered) so Previous and
  // Next in the modal follow the list on screen.
  const [previewRows, setPreviewRows] = useState<AgreementChapter[]>([])
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const previewChapter = previewIndex === null ? null : previewRows[previewIndex] ?? null

  // Which article the user opened last, so coming back from it scrolls to that
  // row instead of the top of the page.
  const focusStorageKey = `rta-chapters-focus-${idAgreement}`
  const [focusRowId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(focusStorageKey)
    } catch {
      return null
    }
  })

  // Before the layout's own scroll-to-top gets a chance to run, since the
  // table is about to scroll to the row instead. Layout effect: the layout
  // schedules that jump from a passive effect, which runs later.
  useLayoutEffect(() => {
    if (focusRowId) suppressScrollTopOnce()
  }, [focusRowId])

  useEffect(() => {
    if (!focusRowId) return

    try {
      // One-shot: only the trip back from an article should scroll.
      sessionStorage.removeItem(focusStorageKey)
    } catch {
      // Storage unavailable - nothing to clean up.
    }
  }, [focusRowId, focusStorageKey])

  const rememberRow = useCallback(
    (id: string) => {
      try {
        sessionStorage.setItem(focusStorageKey, String(id))
      } catch {
        // Storage unavailable - the row just won't be highlighted on return.
      }
    },
    [focusStorageKey]
  )

  const ability = useAbility(AbilityContext)

  const columns: ColumnDef<AgreementChapter, any>[] = useMemo(
    () =>
      [
        columnHelper.accessor('chapter_name', {
          header: 'Chapter Name',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('chapter_number', {
          header: 'Chapter Number',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left font-semibold text-dark',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('article_name', {
          header: 'Article Name',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('article_number', {
          header: 'Article Number',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('chapter_id', {
          header: 'Chapter Id',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('article_id', {
          header: 'Article Id',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('nwords_new', {
          header: 'N Words',
          cell: (info) => info.getValue() ?? 0,
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('id', {
            header: '',
            cell: (info) => {
              const id = info.getValue()
              return (
                <div className='d-flex gap-3'>
                  {ability.can('create', subject) ? (
                    <OverlayTrigger
                      placement='top'
                      overlay={<Tooltip id={`insert-after-${id}`}>Insert article below here</Tooltip>}
                    >
                      <Link
                        to={`/agreement/${idAgreement}/chapter/add?after=${id}`}
                        className='link-primary'
                        onClick={() => rememberRow(id)}
                      >
                        <span className='fa fa-plus'></span>
                      </Link>
                    </OverlayTrigger>
                  ) : (
                    ''
                  )}
                  <OverlayTrigger
                    placement='top'
                    overlay={<Tooltip id={`view-${id}`}>View article</Tooltip>}
                  >
                    <Link
                      to={`/agreement/chapter/${id}`}
                      className='link-primary'
                      onClick={() => rememberRow(id)}
                    >
                      <span className='fa fa-eye'></span>
                    </Link>
                  </OverlayTrigger>
                  {ability.can('delete', subject) ? (
                    <Link to='#' className='link-danger' onClick={() => handleDeleteContent(id)}>
                      <span className='fa fa-trash'></span>
                    </Link>
                  ) : (
                    ''
                  )}
                </div>
              )
            },
            meta: {
              headerClass: 'font-bold',
              cellClass: 'text-end',
            },
            enableSorting: false,
            enableGlobalFilter: true,
          }),
      ].filter(Boolean) as ColumnDef<AgreementChapter, any>[],
    [ability, subject, idAgreement, rememberRow]
  )
  // end of table chapters

  const fetchOptionAgreement = async () => {
    try {
      setIsLoadingAgreement(true)

      const optionsRes = await getOptionAgreements(axiosPrivate)
      const resData = optionsRes.data.data
      setAgreementType(resData.type.map((n: string) => ({value: n, label: n})))
      setAgreementNotification(resData.notification.map((n: string) => ({value: n, label: n})))
      setAgreementComposition(resData.composition)
      setAgreementDocumentType(resData.document_type)
      const allCountries = [
        {
          value: 'EUN',
          label: 'European Union (EUN)',
          region_un: ['Europe'],
          subregion_un: [],
          escap: false,
        },
        {
          value: 'TWN',
          label: 'Other Asia (TWN)',
          region_un: ['Asia Pacific'],
          subregion_un: [],
          escap: false,
        },
        ...resData.parties, // ← backend must return this
      ]
      setCountries(allCountries)

      if (isDetailMode) {
        const detailRes = await getAgreementById(axiosPrivate, {idAgreement})
        const detailResdata = detailRes.data.data
        if (detailResdata) {
          const agreementDetailType = detailResdata.type ? detailResdata.type.split(';') : []

          const agreementDetailNotification = detailResdata.notification
            ? detailResdata.notification.split(';')
            : []

          const agreementDetailParties = detailResdata.iso ? detailResdata.iso.split(';') : []
          const agreementDetailSources = parseSources(detailResdata.sources)

          // Older agreements were saved before `subregion`/`crossregional`
          // existed, or master data was missing region info at save time — backfill
          // them from the stored parties list so they still display, and so the
          // required-field check below (which mirrors the backend's Joi schema)
          // doesn't get satisfied with stale blanks.
          //
          // `region` is a purely client-computed display value (never stored or
          // round-tripped by the backend), so unlike subregion/crossregional it is
          // always recomputed fresh from the current party list, not just backfilled
          // when blank.
          let subregion = detailResdata.subregion
          let crossregional = detailResdata.crossregional
          const derived = deriveFieldsFromParties(
            agreementDetailParties.map((iso: string) => iso.trim()),
            allCountries
          )
          if (!subregion) subregion = derived.subregionArray.join('; ')
          if (!crossregional) crossregional = derived.crossregional
          const derivedRegion = derived.regionArray.join('; ')

          setAgreementDetail((prev) => ({
            ...prev,
            ...detailResdata,
            type: agreementDetailType,
            notification: agreementDetailNotification,
            iso: agreementDetailParties,
            sources: agreementDetailSources,
            subregion,
            crossregional,
            // Parties WTO is now a fully derived, disabled field (item 5) —
            // always recompute from the current party list rather than
            // trusting whatever was last stored.
            parties_wto: agreementDetailParties.length ? derived.partiesWto : '',
          }))
          setRegion(derivedRegion)
          setSources(agreementDetailSources)
          setDataTable(detailResdata.chapters)
          setPendingChapters(detailResdata.pending_chapters)
          // should be add the optionPartiesHere
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingAgreement(false)
    }
  }
  const isEditDisabled = pendingChapters.length > 0

  useEffect(() => {
    fetchOptionAgreement()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idAgreement])

  // Add or Edit
  const handleAgreementChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value, multiple, selectedOptions} = e.target as HTMLSelectElement

    if (multiple) {
      const values = Array.from(selectedOptions, (option) => option.value)
      setAgreementDetail((prev) => ({
        ...prev,
        [name]: values,
      }))
    } else {
      setAgreementDetail((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  // 'WLD' is the pre-existing "World" party used elsewhere in this codebase
  // (national trade-analysis pages default their partner selector to it) —
  // reused here instead of inventing a synthetic value, since it's already
  // a recognized code in dss_economies.
  const handlePartiesChange = (selected: MultiValue<any>) => {
    const selectedValues = selected?.map((s) => s.value) || []

    // 🔥 If nothing selected → reset derived fields
    if (selectedValues.length === 0) {
      setAgreementDetail((prev) => ({
        ...prev,
        iso: [],
        subregion: '',
        crossregional: '',
        parties_escap: '',
        parties_wto: '',
      }))
      setRegion('')
      return
    }

    const {regionArray, subregionArray, crossregional, partiesEscap, partiesWto} =
      deriveFieldsFromParties(selectedValues, countries)

    setAgreementDetail((prev) => ({
      ...prev,
      iso: selectedValues,
      subregion: subregionArray.join('; '),
      crossregional,
      parties_escap: partiesEscap,
      parties_wto: partiesWto,
    }))
    setRegion(regionArray.join('; '))
  }

  // Item 4: selecting "WTO Agreement" as a type is a one-shot convenience
  // fill of Parties (→ World) — it doesn't lock the field, the user can
  // still change parties afterward.
  const handleTypeChange = (selected: MultiValue<any>) => {
    const selectedTypes = selected.map((s) => s.value)
    const newlyAddedWto =
      selectedTypes.includes(WTO_AGREEMENT_TYPE) &&
      !agreementDetail.type?.includes(WTO_AGREEMENT_TYPE)

    if (newlyAddedWto) {
      const {regionArray, subregionArray, crossregional, partiesEscap, partiesWto} =
        deriveFieldsFromParties([WORLD_PARTY], countries)

      setAgreementDetail((prev) => ({
        ...prev,
        type: selectedTypes,
        iso: [WORLD_PARTY],
        subregion: subregionArray.join('; '),
        crossregional,
        parties_escap: partiesEscap,
        parties_wto: partiesWto,
      }))
      setRegion(regionArray.join('; '))
      return
    }

    setAgreementDetail((prev) => ({
      ...prev,
      type: selectedTypes,
    }))
  }

  const validateRequiredFields = () => {
    const requiredFields = [
      'agreement',
      'acronym',
      'type',
      'status',
      'parties_wto',
      'composition',
      'crossregional',
    ]

    const isValid =
      requiredFields.every((key) => {
        const val = (agreementDetail as any)[key]
        if (val === undefined || val === null || String(val).trim() === '') return false

        // Legacy records can carry a status/composition value that predates the
        // current enum (e.g. 'Terminated', a stray tab-prefixed 'Bilateral') — the
        // backend rejects those with a 400, so don't let a stale value pass here.
        if (key === 'status') {
          return AGREEMENT_STATUS_OPTIONS.some((option) => option.label === val)
        }
        if (key === 'composition') {
          return (agreementComposition as string[]).includes(val)
        }

        return true
      }) && region.trim() !== ''

    setIsFormValid(isValid)
  }

  useEffect(() => {
    validateRequiredFields()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementDetail, region])

  const serializeSources = (sources: AgreementSource[], legacySource?: string) => {
    // CASE 1: new multi-source UI
    // A source keeps the plain-URL format (no alias|link pipe) unless it has
    // a distinct alias, so untouched legacy single-URL sources round-trip
    // unchanged instead of getting silently rewritten to `url|url` on every
    // save — see agreement #704, which showed a spurious "sources changed"
    // audit-log entry from a save that never touched Sources.
    if (sources.length > 0) {
      return sources
        .filter((s) => s.link)
        .map((s) => (s.alias && s.alias !== s.link ? `${s.alias}|${s.link}` : s.link))
        .join(';')
    }

    // CASE 2: legacy string → upgrade format
    if (legacySource && legacySource.trim() !== '') {
      const link = legacySource.trim()
      return `${link}|${link}`
    }

    // CASE 3: empty
    return ''
  }

  const handleSubmit = () => {
    const formData = new FormData()

    // Dynamically append all agreementDetail fields
    buildAgreementFormEntries(agreementDetail, [
      'xml_file',
      'id',
      'is_new',
      'chapters',
      'pending_chapters',
      'parties',
      'sources',
      'subregion',
      // These 5 columns still physically exist on dss_trade_agreements (the
      // DROP COLUMN migration is deferred), so GET /:id's SELECT * can leak
      // them back into agreementDetail via the `...detailResdata` spread on
      // load. The backend no longer declares them in its Joi schema, so
      // resubmitting them fails with `"<field>" is not allowed` — skip them
      // unconditionally regardless of whether the column has been dropped.
      'date_of_implementation',
      'region',
      'wits_id',
      'tota_id',
      'aptiad_id',
    ]).forEach(([key, value]) => formData.append(key, value))

    const sourcesString = serializeSources(sources, agreementDetail.sources)
    formData.append('sources', sourcesString)

    // Append file if available
    if (importFile) {
      formData.append('file', importFile)
    }

    // BRA, CHL, EGY, ISR, KOR, MEX, PAR, PRY, PER, PHL, TUN, TUR, URY, SRB, EUN, BGD
    // West Asia; South America; Africa; Middle East; East Asia; North America; Europe
    // Europe; Africa & Middle East; Latin America & Caribbean; Asia Pacific

    showSwal({
      title: `Are you sure you want to ${isEditMode ? 'update' : 'create'} this agreement?`,
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return // exit if user cancels

      setIsLoadingButton(true)

      if (isEditMode) {
        updateAgreementById(axiosPrivate, idAgreement, formData)
          .then((res) => {
            if (res.status === 200) {
              showSwal('Data updated', undefined, 'success').then(() => {
                window.location.reload()
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', getErrorMessage(err, 'Failed to update data'), 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      } else {
        createAgreement(axiosPrivate, formData)
          .then((res) => {
            if (res.status === 200) {
              showSwal('Data created', undefined, 'success').then(() => {
                navigate('/agreement')
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', getErrorMessage(err, 'Failed to create data'), 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate('/agreement')
        } else {
          window.location.reload()
        }
      }
    })
  }
  // End of Add or Edit
  // Contents
  const tableHelpersRef = useRef<any>(null)
  const handleDeleteContent = (id: any) => {
    showSwal({
      title: 'Are you sure you want to delete this content?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteAgreementContentById(axiosPrivate, id)
          .then((res) => {
            if (res.status === 200) {
              tableHelpersRef.current?.skipPageReset()
              showSwal('Data deleted', undefined, 'success')

              // Deleting closes the gap in the article_id / chapter_id
              // sequence server-side, so the rows that are left have to be
              // re-read rather than just dropping this one locally.
              return getAgreementById(axiosPrivate, {idAgreement}).then((detailRes) => {
                const detail = detailRes.data?.data
                if (detail) {
                  setDataTable(detail.chapters)
                  setPendingChapters(detail.pending_chapters)
                }
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', getErrorMessage(err, 'Failed to delete data'), 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  // Export Detail
  const [isExporting, setIsExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  const [exportMessage, setExportMessage] = useState('Exporting data')
  const handleDownloadAgreements = () => {
    setIsExporting(true)

    const body = {
      rta_id: idAgreement,
    }

    exportAgreementDetail(axiosPrivate, body)
      .then((res) => {
        const blob = new Blob([res.data], {type: 'text/csv'})

        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = `agreement_${idAgreement}_export.csv`

        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        showSwal('Export Complete', undefined, 'success')
      })
      .catch((e) => {
        showSwal('Error', getErrorMessage(e, 'Export failed'), 'error')
      })
      .finally(() => {
        setIsExporting(false)
      })
  }
  // End of Export Detail

  const canApprove = ability.can('approve', 'PERM_APPROVE_AGREEMENT')

  return (
    <>
      {isExporting && (
        <div className='sync-overlay'>
          <div className='sync-box'>
            <h4>{exportMessage}</h4>
            <p>
              This export process may take a few minutes, during which the system will be
              temporarily unavailable.
            </p>
          </div>
        </div>
      )}
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Data Agreement',
            path: 'agreement',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isAddMode ? 'Add' : isEditMode ? 'Edit' : agreementDetail.agreement}
      </PageTitle>
      <div className='TradeDataAgreementDetailPage'>
        {canApprove && !isAddMode && pendingChapters && pendingChapters.length > 0 && (
          <div className='alert-no-result-data'>
            <div className='alert alert-warning d-flex align-items-center justify-content-between p-5'>
              <div className='alert-content d-flex align-items-start'>
                <span className='svg-icon svg-icon-2hx svg-icon-warning me-3'>
                  <i className='fa-solid fa-triangle-exclamation'></i>
                </span>
                <div className='d-flex flex-column'>
                  <h5 className='mb-1'>Review Needed</h5>
                  <span>There are chapters that require review or approval:</span>

                  <ul className='mt-2 mb-0'>
                    {pendingChapters.map((chapter: any, index) => (
                      <li key={index}>
                        <a className='text-link' href={`/agreement/review-chapter/${chapter.id}`}>
                          {chapter.article_name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>
                        {isDetailMode ? `Detail ${agreementDetail.agreement}` : 'Add New Agreement'}
                      </h3>
                    </div>
                    <div className='d-flex gap-2 align-items-center'>
                      {isDetailMode && (
                        <Button
                          className='btn-audit-log d-flex gap-2 align-items-center'
                          variant='outline-primary'
                          onClick={() => setShowAuditLogModal(true)}
                        >
                          <span className='fa fa-clock-rotate-left'></span>
                          Audit Log
                        </Button>
                      )}
                      {ability.can('update', subject) && isDetailMode && !isEditMode ? (
                        <OverlayTrigger
                          placement='top'
                          overlay={
                            isEditDisabled ? (
                              <Tooltip id='edit-disabled-tooltip'>
                                Cannot edit while there are chapters pending review
                              </Tooltip>
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span className='d-inline-block'>
                            <Button
                              className='btn btn-primary d-flex gap-4 align-items-center'
                              onClick={() => setIsEditMode(true)}
                              disabled={isEditDisabled}
                              style={{pointerEvents: isEditDisabled ? 'none' : 'auto'}} // important
                            >
                              <span className='fa fa-pencil'></span>
                              Edit
                            </Button>
                          </span>
                        </OverlayTrigger>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoadingAgreement ? (
                  <>
                    {isAddMode || isEditMode ? (
                      <Form className='form-agreement'>
                        <div className='row'>
                          <div className='col-12'>
                            <h3>General Information</h3>
                          </div>
                          <div className='col-md-8'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Name</span>
                            </Form.Label>
                            <Form.Control
                              placeholder='Enter full agreement name'
                              type='text'
                              name='agreement'
                              value={agreementDetail.agreement}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Short Name</span>
                            </Form.Label>
                            <Form.Control
                              type='text'
                              placeholder='Enter short name'
                              name='acronym'
                              value={agreementDetail.acronym}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          {isEditMode && (
                            <div className='col-md-4'>
                              <Form.Label className='text-primary me-0' column>
                                Treaty Identifier TINA
                              </Form.Label>
                              <Form.Control type='text' value={idAgreement || ''} readOnly />
                            </div>
                          )}
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0' column>
                              <span className='required'>Status</span>
                            </Form.Label>
                            <Form.Select
                              name='status'
                              value={agreementDetail.status}
                              onChange={handleAgreementChange}
                            >
                              <option value='' disabled>
                                Select status
                              </option>
                              {AGREEMENT_STATUS_OPTIONS.map((item) => (
                                <option key={item.value} value={item.label}>
                                  {item.label}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0' column>
                              WTO RTA Id
                            </Form.Label>
                            <Form.Control
                              type='text'
                              placeholder='Enter WTO RTA Id'
                              name='wto_rta_id'
                              value={agreementDetail.wto_rta_id}
                              onChange={handleAgreementChange}
                            />
                            <a
                              href='https://rtais.wto.org/UI/PublicAllRTAList.aspx'
                              target='_blank'
                              rel='noreferrer'
                              className='text-link'
                            >
                              check the WTO database
                            </a>
                          </div>
                          <div className='col-md-6'>
                            <Form.Label className='text-primary me-0' column>
                              <span className='required'>Type</span>
                            </Form.Label>
                            <div className='resizable-multiselect'>
                              <Select
                                isMulti
                                name='type'
                                placeholder='Select type(s)'
                                options={agreementType} // now [{ value, label }, ...]
                                value={
                                  agreementDetail.type?.map((n: string) => ({
                                    value: n,
                                    label: n,
                                  })) || []
                                }
                                onChange={handleTypeChange}
                                menuPortalTarget={document.body}
                                styles={{
                                  ...multiSelectResizableStyles,
                                  menuPortal: (base) => ({...base, zIndex: 9999}),
                                }}
                              />
                            </div>
                          </div>
                          <div className='col-md-6'>
                            <Form.Label className='text-primary me-0' column>
                              Notification
                            </Form.Label>
                            <div className='resizable-multiselect'>
                              <Select
                                isMulti
                                name='notification'
                                placeholder='Select notification(s)'
                                options={agreementNotification} // now [{ value, label }, ...]
                                value={
                                  agreementDetail.notification?.map((n: string) => ({
                                    value: n,
                                    label: n,
                                  })) || []
                                }
                                onChange={(selected) => {
                                  setAgreementDetail((prev) => ({
                                    ...prev,
                                    notification: selected.map((s) => s.value), // store only the string array
                                  }))
                                }}
                                menuPortalTarget={document.body}
                                styles={{
                                  ...multiSelectResizableStyles,
                                  menuPortal: (base) => ({...base, zIndex: 9999}),
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <hr />
                        <div className='row'>
                          <div className='col-12'>
                            <h3>Date Information</h3>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Signed
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_signed'
                              value={agreementDetail.date_signed}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Into Force
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_into_force'
                              value={agreementDetail.date_into_force}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Notification
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_notification'
                              value={agreementDetail.date_notification}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              End Implementation
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='end_implementation'
                              value={agreementDetail.end_implementation}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Inactive
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_inactive'
                              value={agreementDetail.date_inactive}
                              onChange={handleAgreementChange}
                            />
                          </div>
                        </div>
                        <hr />
                        <div className='row'>
                          <div className='col-12'>
                            <h3>Participation & Scope</h3>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0' column>
                              Parties
                            </Form.Label>
                            <div className='resizable-multiselect'>
                              <Select
                                isMulti
                                name='notification'
                                placeholder='Select country(s)'
                                options={countries}
                                value={
                                  agreementDetail.iso
                                    ?.map((iso) => countries.find((option) => option.value === iso))
                                    .filter(Boolean) || []
                                }
                                onChange={handlePartiesChange}
                                menuPortalTarget={document.body}
                                styles={{
                                  ...multiSelectResizableStyles,
                                  menuPortal: (base) => ({...base, zIndex: 9999}),
                                }}
                              />
                            </div>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Composition</span>
                            </Form.Label>
                            <Form.Select
                              name='composition'
                              value={agreementDetail.composition}
                              onChange={handleAgreementChange}
                            >
                              <option value='' disabled>
                                Select composition
                              </option>
                              {agreementComposition.map((item) => (
                                <option key={item} value={item}>
                                  {item}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Parties WTO
                            </Form.Label>
                            <Form.Control
                              className='text-capitalize'
                              type='text'
                              name='parties_wto'
                              value={agreementDetail.parties_wto}
                              placeholder='Please select parties'
                              disabled
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Region
                            </Form.Label>
                            <textarea
                              className='form-control mb-3'
                              rows={2}
                              data-kt-element='input'
                              placeholder='Please select parties'
                              name='region'
                              value={region}
                              disabled
                            ></textarea>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Subregion
                            </Form.Label>
                            <textarea
                              className='form-control mb-3'
                              rows={2}
                              data-kt-element='input'
                              placeholder='Please select parties'
                              name='subregion'
                              value={agreementDetail.subregion}
                              disabled
                            ></textarea>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Crossregional
                            </Form.Label>
                            <Form.Control
                              type='text'
                              className='text-capitalize'
                              name='crossregional'
                              value={agreementDetail.crossregional}
                              placeholder='Please select parties'
                              disabled
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Parties ESCAP
                            </Form.Label>
                            <Form.Control
                              className='text-capitalize'
                              type='text'
                              name='parties_escap'
                              value={agreementDetail.parties_escap}
                              placeholder='Please select parties'
                              disabled
                            />
                          </div>
                        </div>
                        <hr />
                        <div className='row'>
                          <div className='col-12'>
                            <h3>Others</h3>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Language
                            </Form.Label>
                            <Form.Control
                              type='text'
                              name='language'
                              placeholder='Enter language code (e.g. EN)'
                              value={agreementDetail.language}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Service Schedule
                            </Form.Label>
                            <Form.Select
                              name='services_schedules'
                              value={agreementDetail.services_schedules}
                              onChange={handleAgreementChange}
                            >
                              <option value='' disabled>
                                Select service schedule
                              </option>
                              {agreementDetail.services_schedules && (
                                <option value=''>-- Clear selection --</option>
                              )}
                              <option value='yes'>Yes</option>
                              <option value='no'>No</option>
                            </Form.Select>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Desta Id
                            </Form.Label>
                            <Form.Control
                              type='text'
                              name='desta_id'
                              placeholder='Enter Desta Id'
                              value={agreementDetail.desta_id}
                              onChange={handleAgreementChange}
                            />
                          </div>
                        </div>
                        <hr />
                        <div className='row align-items-end'>
                          <div className='col-lg-12 d-flex align-items-center justify-content-between'>
                            <h3>Sources</h3>
                            <Button onClick={() => setShowSourceModal(true)}>Add Source</Button>
                          </div>
                          <div className='col-lg-12'>
                            <DataTable<AgreementSource>
                              data={sources}
                              columns={sourceColumns}
                              enablePagination={false}
                              enableSorting={true}
                              enableGlobalFilter={false}
                              className='my-custom-table-wrapper'
                            />
                          </div>
                        </div>
                        <div className='row pt-5'>
                          <div className='col-lg-12 d-flex justify-content-end gap-3'>
                            <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button
                              variant='primary'
                              onClick={handleSubmit}
                              disabled={isLoadingButton || !isFormValid}
                            >
                              {isLoadingButton ? (
                                <>
                                  <Spinner animation='border' size='sm' className='me-2' />
                                  Submit
                                </>
                              ) : (
                                'Submit'
                              )}
                            </Button>
                          </div>
                        </div>
                      </Form>
                    ) : (
                      <AgreementDetailView
                        agreementDetail={agreementDetail}
                        idAgreement={idAgreement}
                        region={region}
                      />
                    )}
                  </>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {idAgreement && !isEditMode ? (
          <Row>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100'>
                    <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                      <div className='title d-flex align-items-center'>
                        <span className='fa fa-file-alt px-2'></span>
                        <h3 className='text-gray-700'>List Content</h3>
                      </div>
                      <div className='header-action'>
                        <Button
                          className='btn btn-primary btn-outline'
                          onClick={() => {
                            handleDownloadAgreements()
                          }}
                        >
                          <span className='fa fa-download px-2'></span>
                          Export CSV
                        </Button>
                        <Link
                          to={`/agreement/${idAgreement}/chapter/add`}
                          className='btn btn-primary d-flex gap-4 align-items-center'
                        >
                          <span className='fa fa-plus'></span>
                          Add
                        </Link>
                        {dataTable.length > 1 && (
                          <OverlayTrigger
                            placement='top'
                            overlay={
                              isEditDisabled ? (
                                <Tooltip id='edit-disabled-tooltip'>
                                  Cannot edit while there are chapters pending review
                                </Tooltip>
                              ) : (
                                <></>
                              )
                            }
                          >
                            <span className='d-inline-block'>
                              <Button
                                onClick={() =>
                                  navigate(`/agreement/${idAgreement}/chapter/edit-order`)
                                }
                                variant='outline-primary'
                                disabled={isEditDisabled}
                                style={{pointerEvents: isEditDisabled ? 'none' : 'auto'}} // important
                              >
                                Edit Order
                              </Button>
                            </span>
                          </OverlayTrigger>
                        )}
                      </div>
                    </div>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <DataTable<AgreementChapter>
                    data={dataTable}
                    columns={columns}
                    enablePagination={true}
                    enableSorting={true}
                    enableGlobalFilter={true}
                    globalFilterPlaceholder='Search Article Name'
                    defaultPageSize={10}
                    pageSizes={[5, 10, 20, 50, 100]}
                    className='my-custom-table-wrapper'
                    persistKey={`rta-chapters-${idAgreement}`}
                    focusRowId={focusRowId}
                    getRowKey={(row) => String(row.id)}
                    onRowClick={(row, visibleRows) => {
                      setPreviewRows(visibleRows)
                      setPreviewIndex(visibleRows.indexOf(row))
                    }}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          // <NoPermissionCard />
          ''
        )}
      </div>
      <ModalArticleText
        show={Boolean(previewChapter)}
        onHide={() => setPreviewIndex(null)}
        chapter={previewChapter}
        position={previewIndex === null ? undefined : previewIndex + 1}
        total={previewRows.length}
        hasPrev={previewIndex !== null && previewIndex > 0}
        hasNext={previewIndex !== null && previewIndex < previewRows.length - 1}
        onPrev={() => setPreviewIndex((prev) => (prev === null ? prev : Math.max(0, prev - 1)))}
        onNext={() =>
          setPreviewIndex((prev) =>
            prev === null ? prev : Math.min(previewRows.length - 1, prev + 1)
          )
        }
      />
      <ModalAddSources
        show={showSourceModal}
        onHide={() => setShowSourceModal(false)}
        onSubmit={() => {
          handleAddSource()
          setShowSourceModal(false)
        }}
        alias={sourceAlias}
        link={sourceLink}
        onAliasChange={setSourceAlias}
        onLinkChange={setSourceLink}
      />
      <ModalAuditLog
        show={showAuditLogModal}
        onHide={() => setShowAuditLogModal(false)}
        tableName='dss_trade_agreements'
        recordId={idAgreement}
      />
    </>
  )
}

export default TradeDataAgreementDetailPage
